import { ProjectStatus } from "../index";
import { KanbanDefaultStates } from "../../ProjectList/CanvasKanban";
import { STATE_KEY_TO_STATUS_ID } from "../constants";

function matchesStatusKey(
  status: ProjectStatus,
  key: string,
  name?: string,
): boolean {
  return (
    status.defaultStatusKey === key ||
    status.id === key ||
    (name !== undefined && status.name === name)
  );
}

export interface ColumnConfig {
  key: string;
  name: string;
  type: string;
  isDefault: boolean;
  order: number;
}

export function getDefaultColumnConfig(): ColumnConfig[] {
  const defaultOrder: Array<{ key: string; name: string; type: string }> = [
    { key: "BACKLOG", name: KanbanDefaultStates.BACKLOG, type: "backlog" },
    { key: "TODO", name: KanbanDefaultStates.TODO, type: "todo" },
    {
      key: "IN_PROGRESS",
      name: KanbanDefaultStates.IN_PROGRESS,
      type: "in-progress",
    },
    {
      key: "IN_REVIEW",
      name: KanbanDefaultStates.IN_REVIEW,
      type: "in-review",
    },
    { key: "BLOCKED", name: KanbanDefaultStates.BLOCKED, type: "blocked" },
    { key: "DONE", name: KanbanDefaultStates.DONE, type: "done" },
    { key: "CANCELED", name: KanbanDefaultStates.CANCELED, type: "canceled" },
  ];

  return defaultOrder.map((item, index) => ({
    key: item.key,
    name: item.name,
    type: item.type,
    isDefault: true,
    order: index,
  }));
}

export function hasCustomColumnConfig(
  projectStatuses: ProjectStatus[] | undefined | null,
): boolean {
  if (!projectStatuses?.length) {
    return false;
  }

  const defaultKeys = Object.keys(KanbanDefaultStates);
  return projectStatuses.some(
    (status) =>
      status.isDefault &&
      defaultKeys.some((key) => {
        const defaultName =
          KanbanDefaultStates[key as keyof typeof KanbanDefaultStates];
        return (
          status.defaultStatusKey === key ||
          status.id === key ||
          status.name === defaultName
        );
      }),
  );
}

export function getColumnConfig(
  projectStatuses: ProjectStatus[] | undefined | null,
): ColumnConfig[] {
  const allStatuses = projectStatuses || [];
  const hasCustomStatuses = allStatuses.some((s) => !s.isDefault);
  const hasCustomColConfig = hasCustomColumnConfig(projectStatuses);

  if (!hasCustomStatuses && !hasCustomColConfig) {
    return getDefaultColumnConfig();
  }

  const columnConfigs: ColumnConfig[] = [];
  const defaultStatuses = allStatuses.filter((s) => s.isDefault);
  const customStatuses = allStatuses.filter((s) => !s.isDefault);
  const defaultConfig = getDefaultColumnConfig();

  defaultConfig.forEach((defaultCol) => {
    const customizedDefault = defaultStatuses.find(
      (s) =>
        s.isDefault && matchesStatusKey(s, defaultCol.key, defaultCol.name),
    );

    if (customizedDefault) {
      columnConfigs.push({
        key: customizedDefault.id,
        name: customizedDefault.name,
        type: customizedDefault.type,
        isDefault: true,
        order: customizedDefault.order ?? columnConfigs.length,
      });
    } else {
      columnConfigs.push({
        ...defaultCol,
        order: columnConfigs.length,
      });
    }
  });

  // Build type groups once - O(n)
  const typeGroups = new Map<string, ProjectStatus[]>();
  customStatuses.forEach((status) => {
    if (!typeGroups.has(status.type)) {
      typeGroups.set(status.type, []);
    }
    typeGroups.get(status.type)!.push(status);
  });

  // Optimized O(n) single-pass insertion algorithm
  // Build result array with custom statuses inserted after their corresponding defaults
  const result: ColumnConfig[] = [];

  // Pre-sort custom statuses by type and order for efficient insertion
  const sortedCustomStatusesByType = new Map<string, ProjectStatus[]>();
  typeGroups.forEach((statuses, type) => {
    sortedCustomStatusesByType.set(
      type,
      statuses.sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    );
  });

  // Single pass: iterate through defaults and insert custom statuses after each default
  columnConfigs.forEach((config) => {
    // Add the default config first
    result.push({
      ...config,
      order: result.length,
    });

    // If this default has custom statuses of the same type, insert them right after
    if (config.isDefault) {
      const customStatusesForType = sortedCustomStatusesByType.get(config.type);
      if (customStatusesForType) {
        customStatusesForType.forEach((status) => {
          result.push({
            key: status.id,
            name: status.name,
            type: status.type,
            isDefault: false,
            order: result.length,
          });
        });
        // Remove from map to avoid duplicate insertion
        sortedCustomStatusesByType.delete(config.type);
      }
    }
  });

  // Handle custom statuses for types that don't have defaults (append at end)
  sortedCustomStatusesByType.forEach((statuses) => {
    statuses.forEach((status) => {
      result.push({
        key: status.id,
        name: status.name,
        type: status.type,
        isDefault: false,
        order: result.length,
      });
    });
  });

  return result;
}

export function migrateDefaultsToProjectStatuses(
  existingStatuses: ProjectStatus[] | undefined | null,
  defaultStatusKey: string,
  newName?: string,
  newOrder?: number,
): ProjectStatus[] {
  const statuses = existingStatuses || [];
  const defaultName =
    KanbanDefaultStates[defaultStatusKey as keyof typeof KanbanDefaultStates];

  const seenKeys = new Set<string>();
  const deduplicatedStatuses = statuses.filter((s) => {
    if (s.isDefault && matchesStatusKey(s, defaultStatusKey)) {
      if (seenKeys.has(defaultStatusKey)) {
        return false;
      }
      seenKeys.add(defaultStatusKey);
      return true;
    }
    return true;
  });

  const existingDefaultStatus = deduplicatedStatuses.find(
    (s) => s.isDefault && matchesStatusKey(s, defaultStatusKey),
  );

  if (existingDefaultStatus) {
    return deduplicatedStatuses.map((s) =>
      matchesStatusKey(s, defaultStatusKey) && s.isDefault
        ? {
            ...s,
            id: s.id.includes("-") ? s.id : crypto.randomUUID(),
            name: newName ?? s.name,
            order: newOrder ?? s.order ?? 0,
            defaultStatusKey: defaultStatusKey,
            updatedAt: new Date().toISOString(),
          }
        : s,
    );
  }

  const defaultStatusType = defaultStatusKey.toLowerCase().replace(/_/g, "-");
  const newDefaultStatus: ProjectStatus = {
    id: crypto.randomUUID(),
    name: newName ?? defaultName,
    type: defaultStatusType,
    isDefault: true,
    defaultStatusKey: defaultStatusKey,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    order: newOrder ?? deduplicatedStatuses.length,
  };

  return [...deduplicatedStatuses, newDefaultStatus];
}

export function updateColumnOrder(
  statuses: ProjectStatus[],
  newOrder: Array<{ key: string; isDefault: boolean }>,
): ProjectStatus[] {
  const statusMap = new Map<string, ProjectStatus>();
  statuses.forEach((s) => {
    statusMap.set(s.id, s);
  });

  return newOrder.map((item, index) => {
    const status = statusMap.get(item.key);
    if (!status) {
      const fallbackStatus = statuses.find((s) => s.id === item.key);
      if (!fallbackStatus) {
        console.error(`Status not found for key: ${item.key}`);
        return statuses[0];
      }
      return {
        ...fallbackStatus,
        order: index,
        updatedAt: new Date().toISOString(),
      };
    }
    return {
      ...status,
      order: index,
      updatedAt: new Date().toISOString(),
    };
  });
}
